/**
 * ASN list (the "ASN" sub-tab under the Purchasing → ASN main tab).
 *
 * This is the former contents of ASNTab.tsx: the filterable, sortable,
 * paginated list of Advanced Shipping Notices with export and create. The
 * table markup lives in {@link ASNListTable} to keep this file under the
 * 500-line limit.
 */
import { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../../ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { Loader2, Truck, Download } from 'lucide-react';
import { purchasingAPI, coreAPI, customersAPI, AdvancedShippingNotice, CommodityGroup, CommodityType, CompanyGroup, CustomerProfile } from '../../../lib/api';
import { ASNDetailModal } from '../ASNModals';
import type { ASNDetailTab } from '../asnModalTypes';
import { Pagination } from '../../ui/pagination';
import { useView } from '../../../contexts/ViewContext';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import { useIsCustomer } from '../../../hooks/usePortalMode';
import { getDeliveryLineCustomerColumnLabel } from '../../../lib/portalLabels';
import { ASNFilterSection } from './ASNFilterSection';
import { ASNListTable, SortConfig } from './ASNListTable';
import { useASNBulkDelivered } from './useASNBulkDelivered';
import { ASNBulkActionBar } from './ASNBulkActionBar';

/** Imperative handle so a parent (the sub-tab wrapper) can force a refresh. */
export interface ASNListTabHandle {
  refresh: () => void;
}

/** Status tabs shown to suppliers (full workflow). */
const SUPPLIER_ASN_STATUS_TABS = [
  { value: 'all', label: 'All' },
  { value: 'created', label: 'Created' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'delivered', label: 'Delivered' },
] as const;

/** Customer portal only needs inbound visibility — hide draft/submitted states. */
const CUSTOMER_ASN_STATUS_TABS = [
  { value: 'all', label: 'All' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'delivered', label: 'Delivered' },
] as const;

/** Statuses shown when a customer selects the "All" tab (excludes Created/Submitted). */
const CUSTOMER_ASN_ALL_STATUSES = 'in_transit,delivered';

const ASNListTab = forwardRef<ASNListTabHandle>((_props, ref) => {
  const { getViewQueryParams } = useView();
  const { isSupplierPortalMode } = useMenuSettings();
  const isCustomer = useIsCustomer();
  const counterpartyLabel = getDeliveryLineCustomerColumnLabel();
  const statusTabs = isCustomer ? CUSTOMER_ASN_STATUS_TABS : SUPPLIER_ASN_STATUS_TABS;

  const [asns, setAsns] = useState<AdvancedShippingNotice[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailInitialTab, setDetailInitialTab] = useState<ASNDetailTab>('lines');
  const [selectedAsn, setSelectedAsn] = useState<AdvancedShippingNotice | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [pageSize, setPageSize] = useState(50);

  const [sortConfig, setSortConfig] = useState<SortConfig>({ column: 'created_at', direction: 'desc' });

  const [textInputs, setTextInputs] = useState({
    asnNumber: '',
    customer: '',
    trackingNumber: '',
    poNumber: '',
    poLineNumber: '',
  });

  const [filters, setFilters] = useState({
    asnNumber: '',
    customer: '',
    trackingNumber: '',
    poNumber: '',
    poLineNumber: '',
    status: '',
    courier: '',
    courierDeliveryStatus: '',
    dateFrom: '',
    dateTo: '',
    site: '',
    customerGroup: '',
    commodityGroup: '',
    commodityType: '',
    strategic: '',
  });

  const [siteOptions, setSiteOptions] = useState<{ value: string; label: string }[]>([]);
  const [customerGroupOptions, setCustomerGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [commodityGroupOptions, setCommodityGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [commodityTypeOptions, setCommodityTypeOptions] = useState<{ value: string; label: string }[]>([]);
  const [customerOptions, setCustomerOptions] = useState<{ value: string; label: string }[]>([]);
  const [courierOptions, setCourierOptions] = useState<{ value: string; label: string }[]>([]);

  // No filter persistence — always start fresh.
  useEffect(() => {
    setInitialLoadComplete(true);
    Promise.all([
      purchasingAPI.getASNFilterOptions().catch(() => ({ data: { couriers: [] } })),
      purchasingAPI.getLineFilterOptions().catch(() => ({ data: { suppliers: [], customers: [], commodity_groups: [], commodity_types: [] } })),
      purchasingAPI.getCommodityGroups({ enabled: true }).catch(() => ({ data: [] })),
      purchasingAPI.getCommodityTypes({ enabled: true }).catch(() => ({ data: [] })),
      coreAPI.getCustomerGroupsForFilter().catch(() => ({ data: { results: [] } })),
      customersAPI.getProfiles({ page_size: 1000, is_active: true }).catch(() => ({ data: { results: [] } })),
    ]).then(([courierOptsRes, filterOptsRes, groupsRes, typesRes, companyGroupsRes, customersRes]) => {
      setCourierOptions(
        (courierOptsRes.data.couriers || []).map((c) => ({ value: c.value, label: c.label })),
      );

      const suppliers = filterOptsRes.data.suppliers || [];
      setSiteOptions(suppliers.map((c) => ({ value: String(c.id), label: c.name })));

      const groups = Array.isArray(groupsRes.data) ? groupsRes.data : (groupsRes.data as unknown as { results: CommodityGroup[] }).results || [];
      setCommodityGroupOptions(groups.filter((g: CommodityGroup) => g.enabled).map((g: CommodityGroup) => ({ value: String(g.id), label: g.name })));

      const types = Array.isArray(typesRes.data) ? typesRes.data : (typesRes.data as unknown as { results: CommodityType[] }).results || [];
      setCommodityTypeOptions(types.filter((t: CommodityType) => t.enabled).map((t: CommodityType) => ({ value: String(t.id), label: t.name })));

      const customers = (Array.isArray(customersRes.data) ? customersRes.data : (customersRes.data as unknown as { results: CustomerProfile[] }).results || []) as CustomerProfile[];
      setCustomerOptions(customers.map((s) => ({ value: String(s.company), label: s.company_name })));

      const companyGroups = (companyGroupsRes.data as unknown as { results: CompanyGroup[] }).results || [];
      setCustomerGroupOptions(companyGroups.map((g: CompanyGroup) => ({ value: String(g.id), label: g.name })));
    });
  }, []);

  // Customers don't see Created/Submitted tabs — fall back if one was active.
  useEffect(() => {
    if (isCustomer && (activeTab === 'created' || activeTab === 'submitted')) {
      setActiveTab('all');
    }
  }, [isCustomer, activeTab]);

  // Debounce text inputs.
  useEffect(() => {
    if (!initialLoadComplete) return;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        asnNumber: textInputs.asnNumber,
        trackingNumber: textInputs.trackingNumber,
        poNumber: textInputs.poNumber,
        poLineNumber: textInputs.poLineNumber,
      }));
    }, 500);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [textInputs, initialLoadComplete]);

  useEffect(() => {
    if (initialLoadComplete) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters, activeTab, initialLoadComplete, sortConfig, pageSize]);

  const buildFilterParams = (): Record<string, string | number | boolean> => {
    const queryParams: Record<string, string | number | boolean> = {};
    if (filters.asnNumber) queryParams['asn_number__icontains'] = filters.asnNumber;
    if (filters.customer) queryParams['customer'] = filters.customer;
    if (filters.trackingNumber) queryParams['tracking_number__icontains'] = filters.trackingNumber;
    if (filters.courier) queryParams['courier'] = filters.courier;
    if (filters.status) {
      queryParams['status'] = filters.status;
    } else if (activeTab !== 'all') {
      queryParams['status'] = activeTab;
    } else if (isCustomer) {
      queryParams['status__in'] = CUSTOMER_ASN_ALL_STATUSES;
    }
    if (filters.dateFrom) queryParams['expected_delivery_date__gte'] = filters.dateFrom;
    if (filters.dateTo) queryParams['expected_delivery_date__lte'] = filters.dateTo;
    if (filters.poNumber) queryParams['schedules__po_line__po__po_number__icontains'] = filters.poNumber;
    if (filters.poLineNumber) queryParams['schedules__po_line__line_number'] = filters.poLineNumber;
    if (filters.site) queryParams['schedules__po_line__po__supplier'] = filters.site;
    if (filters.customerGroup) queryParams['customer__group'] = filters.customerGroup;
    if (filters.commodityGroup) queryParams['schedules__po_line__commodity_group'] = filters.commodityGroup;
    if (filters.commodityType) queryParams['schedules__po_line__commodity_type'] = filters.commodityType;
    if (filters.courierDeliveryStatus) queryParams['courier_delivery_status'] = filters.courierDeliveryStatus;
    if (filters.strategic === 'yes') queryParams['customer__is_strategic'] = true;
    return queryParams;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const ordering = sortConfig.direction === 'asc' ? sortConfig.column : `-${sortConfig.column}`;
      const queryParams: Record<string, string | number | boolean> = {
        ...buildFilterParams(),
        ordering,
        page: currentPage,
        page_size: pageSize,
      };
      const response = await purchasingAPI.getASNs(queryParams);
      setAsns(response.data.results);
      setTotalCount(response.data.count);
      setTotalPages(Math.ceil(response.data.count / pageSize));
    } catch (err: unknown) {
      console.error('Error fetching ASN data:', err);
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr.response?.data?.detail || 'Failed to load ASN data');
    } finally {
      setLoading(false);
    }
  };

  // Let the sub-tab wrapper refresh this list after a create. Always re-fetch:
  // the initial load has run by then, and a stale guard must never turn the
  // refresh into a no-op and hide a just-created ASN.
  useImperativeHandle(ref, () => ({ refresh: () => { fetchData(); } }));

  // Multi-select + bulk "Mark Delivered" for in-transit ASNs. Calls the
  // per-ASN mark-delivered endpoint once per selection, then refreshes.
  const {
    selectedIds,
    marking,
    isSelectable,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    markSelectedDelivered,
  } = useASNBulkDelivered({ asns, onRefresh: () => { fetchData(); } });

  const clearFilters = () => {
    setTextInputs({ asnNumber: '', customer: '', trackingNumber: '', poNumber: '', poLineNumber: '' });
    setFilters({
      asnNumber: '', customer: '', trackingNumber: '', poNumber: '', poLineNumber: '',
      status: '', courier: '', courierDeliveryStatus: '', dateFrom: '', dateTo: '',
      site: '', customerGroup: '', commodityGroup: '', commodityType: '', strategic: '',
    });
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleFilterChange = (key: string, value: string) => {
    if (['asnNumber', 'trackingNumber', 'poNumber', 'poLineNumber'].includes(key)) {
      setTextInputs({ ...textInputs, [key]: value });
    } else if (key === 'commodityGroup') {
      setFilters({ ...filters, commodityGroup: value, commodityType: '' });
    } else {
      setFilters({ ...filters, [key]: value });
    }
    setCurrentPage(1);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setCurrentPage(1);
    clearSelection();
  };

  const handleSort = (column: string) => {
    setSortConfig(prev => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'desc' };
    });
    setCurrentPage(1);
  };

  const handleViewDetails = (asn: AdvancedShippingNotice) => {
    setSelectedAsn(asn);
    setDetailInitialTab('lines');
    setShowDetailModal(true);
  };

  /** Open an ASN straight on its courier tracking timeline. */
  const handleViewTracking = (asn: AdvancedShippingNotice) => {
    setSelectedAsn(asn);
    setDetailInitialTab('tracking');
    setShowDetailModal(true);
  };

  /**
   * Build the same filter set fetchData() uses, then call the export endpoint
   * so what the user sees on screen is exactly what they download.
   */
  const handleExport = async (fileFormat: 'csv' | 'xlsx' = 'csv') => {
    try {
      setExporting(true);
      const queryParams: Record<string, string | number | boolean> = {
        file_format: fileFormat,
        ...buildFilterParams(),
      };
      const viewParams = getViewQueryParams();
      if (viewParams['site']) queryParams['site'] = String(viewParams['site']);
      if (viewParams['group']) queryParams['group'] = String(viewParams['group']);
      if (viewParams['company']) queryParams['company'] = String(viewParams['company']);

      const response = await purchasingAPI.exportASNs(queryParams);
      const blob = new Blob([response.data], {
        type: fileFormat === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `asn_export_${new Date().toISOString().split('T')[0]}.${fileFormat}`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      toast.success('ASN export completed');
    } catch (err: unknown) {
      console.error('ASN export failed:', err);
      toast.error('Failed to export ASNs');
    } finally {
      setExporting(false);
    }
  };

  const { formatDate: contextFormatDate, formatDateTime: contextFormatDateTime } = useFormatters();
  const formatDate = (dateString: string) => (!dateString ? '-' : contextFormatDate(dateString));
  const formatDateTime = (dateString: string) => (!dateString ? '-' : contextFormatDateTime(dateString));

  if (loading && asns.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">Loading ASNs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error: {error}</p>
            <button onClick={fetchData} className="mt-2 text-sm text-red-600 hover:text-red-800 underline">
              Try again
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {showFilters && (
        <ASNFilterSection
          filters={filters}
          textInputs={textInputs}
          siteOptions={siteOptions}
          customerOptions={customerOptions}
          customerGroupOptions={customerGroupOptions}
          commodityGroupOptions={commodityGroupOptions}
          commodityTypeOptions={commodityTypeOptions}
          courierOptions={courierOptions}
          isCustomer={isCustomer}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
          onSearch={() => fetchData()}
          onHideFilters={() => setShowFilters(false)}
        />
      )}

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold">Advanced Shipping Notices</h3>
              <span className="text-sm text-muted-foreground">
                {totalCount} {totalCount === 1 ? 'ASN' : 'ASNs'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                  showFilters ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                }`}
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </button>
              <button
                onClick={() => handleExport('csv')}
                disabled={exporting || asns.length === 0}
                className="px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                title="Export filtered ASNs to CSV"
              >
                {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                CSV
              </button>
              <button
                onClick={() => handleExport('xlsx')}
                disabled={exporting || asns.length === 0}
                className="px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                title="Export filtered ASNs to Excel"
              >
                {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                Excel
              </button>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList
              className={`grid w-full ${isCustomer ? 'grid-cols-3' : 'grid-cols-5'}`}
              data-tour="asn-status-tabs"
            >
              {statusTabs.map(({ value, label }) => (
                <TabsTrigger key={value} value={value}>{label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      <ASNBulkActionBar
        selectedCount={selectedIds.size}
        marking={marking}
        onClear={clearSelection}
        onMarkDelivered={markSelectedDelivered}
      />

      <Card data-tour="asn-table">
        <CardContent className="p-0">
          {asns.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No ASNs found matching your criteria.
            </div>
          ) : (
            <>
              <div className="px-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  itemsPerPage={pageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  itemLabel="ASNs"
                />
              </div>

              <ASNListTable
                asns={asns}
                sortConfig={sortConfig}
                counterpartyLabel={counterpartyLabel}
                isSupplierPortalMode={isSupplierPortalMode}
                onSort={handleSort}
                onViewDetails={handleViewDetails}
                onViewTracking={handleViewTracking}
                onEdited={fetchData}
                formatDate={formatDate}
                formatDateTime={formatDateTime}
                selectedIds={selectedIds}
                isSelectable={isSelectable}
                onToggleSelect={toggleSelect}
                onToggleSelectAll={toggleSelectAll}
              />

              <div className="px-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  itemsPerPage={pageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  itemLabel="ASNs"
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {loading && (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {showDetailModal && selectedAsn && (
        <ASNDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedAsn(null);
          }}
          asn={selectedAsn}
          initialTab={detailInitialTab}
          onSubmit={async () => await fetchData()}
        />
      )}
    </div>
  );
});

ASNListTab.displayName = 'ASNListTab';

export default ASNListTab;
